/**
 * Chat do assistente dentro da ficha da proposta. A IA responde e pode
 * PROPOR mudanças nos campos editáveis e no título; nada é aplicado sem o
 * vendedor clicar. Histórico fica no sessionStorage por proposta — é
 * conversa de trabalho, não registro.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Send, Loader2, Check, X, RotateCcw, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";

type Msg = { role: "user" | "assistant"; content: string; changes?: { title?: string; values: Record<string, string> } };

const SUGGESTIONS = [
  "Deixe a apresentação mais curta e direta",
  "Tom mais formal, adequado a órgão público",
  "Reforce o escopo com os produtos da negociação",
  "Resuma esta proposta em 3 linhas para eu mandar no WhatsApp",
];

function storageKey(id: number) {
  return `lc:proposal-chat:${id}`;
}

export default function ProposalAssistant({
  proposalId,
  editable,
  title,
  values,
  labels,
  onApply,
  onApplyAndRegenerate,
}: {
  proposalId: number;
  editable: boolean;
  title: string;
  values: Record<string, string>;
  labels: Record<string, string>;
  onApply: (changes: { title?: string; values: Record<string, string> }) => void;
  onApplyAndRegenerate: (changes: { title?: string; values: Record<string, string> }) => void;
}) {
  const [messages, setMessages] = useState<Msg[]>(() => {
    try {
      const raw = sessionStorage.getItem(storageKey(proposalId));
      return raw ? (JSON.parse(raw) as Msg[]) : [];
    } catch {
      return [];
    }
  });
  const [input, setInput] = useState("");
  const [applied, setApplied] = useState<Set<number>>(new Set());
  const listRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    try { sessionStorage.setItem(storageKey(proposalId), JSON.stringify(messages.slice(-30))); } catch { /* sem storage */ }
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [messages, proposalId]);

  const chat = trpc.crm.proposals.chat.useMutation({
    onSuccess: (r) => {
      setMessages((m) => [...m, { role: "assistant", content: r.reply, changes: r.changes.title || Object.keys(r.changes.values).length ? r.changes : undefined }]);
    },
    onError: (e) => {
      toast.error(toUserFriendlyError(e, "O assistente não respondeu"));
      setMessages((m) => m.slice(0, -1)); // devolve a pergunta ao campo
    },
  });

  const send = (text: string) => {
    const content = text.trim();
    if (!content || chat.isPending) return;
    const next: Msg[] = [...messages, { role: "user", content }];
    setMessages(next);
    setInput("");
    chat.mutate({ id: proposalId, messages: next.slice(-20).map(({ role, content }) => ({ role, content })) });
  };

  const hasChanges = (c?: Msg["changes"]) => !!c && (!!c.title || Object.keys(c.values).length > 0);
  const lastChangeIdx = useMemo(() => { for (let i = messages.length - 1; i >= 0; i--) if (hasChanges(messages[i].changes)) return i; return -1; }, [messages]);

  return (
    <div className="rounded-lg border bg-muted/30 flex flex-col">
      <div className="flex items-center justify-between px-3 py-2 border-b">
        <div className="text-sm font-medium flex items-center gap-1.5"><Sparkles className="h-4 w-4 text-primary" /> Assistente</div>
        {messages.length > 0 && (
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setMessages([]); setApplied(new Set()); }}>
            <RotateCcw className="h-3 w-3 mr-1" /> Limpar
          </Button>
        )}
      </div>

      <div ref={listRef} className="max-h-[340px] overflow-y-auto px-3 py-2 space-y-2">
        {messages.length === 0 && (
          <div className="space-y-1.5">
            <p className="text-xs text-muted-foreground">Peça ajustes nos textos ou tire dúvidas sobre a proposta. Eu proponho, você confirma.</p>
            <div className="flex flex-wrap gap-1">
              {SUGGESTIONS.map((s) => (
                <button key={s} type="button" className="text-[11px] rounded-full border px-2 py-0.5 hover:bg-accent text-left" onClick={() => send(s)}>{s}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`text-sm ${m.role === "user" ? "text-right" : ""}`}>
            <div className={`inline-block max-w-[95%] rounded-lg px-3 py-1.5 whitespace-pre-wrap text-left ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-background border"}`}>
              {m.content}
            </div>
            {m.role === "assistant" && hasChanges(m.changes) && (
              <div className="mt-1.5 rounded-md border border-primary/30 bg-background p-2 space-y-2">
                <div className="text-[11px] font-medium text-primary flex items-center gap-1"><Wand2 className="h-3 w-3" /> Alterações propostas</div>
                {m.changes!.title && (
                  <div className="text-xs"><span className="text-muted-foreground">Título:</span> <span className="line-through opacity-60">{title}</span> → <strong>{m.changes!.title}</strong></div>
                )}
                {Object.entries(m.changes!.values).map(([k, v]) => (
                  <div key={k} className="text-xs">
                    <div className="text-muted-foreground">{labels[k] ?? k}</div>
                    <div className="rounded bg-muted/60 p-1.5 mt-0.5 line-through opacity-60 max-h-16 overflow-hidden whitespace-pre-wrap">{values[k] || "(vazio)"}</div>
                    <div className="rounded bg-emerald-50 dark:bg-emerald-950/30 p-1.5 mt-0.5 whitespace-pre-wrap">{v}</div>
                  </div>
                ))}
                {editable && !applied.has(i) && i === lastChangeIdx && (
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    <Button size="sm" className="h-7 text-xs" onClick={() => { onApplyAndRegenerate(m.changes!); setApplied((s) => new Set(s).add(i)); }}>
                      <Check className="h-3 w-3 mr-1" /> Aplicar e regenerar
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { onApply(m.changes!); setApplied((s) => new Set(s).add(i)); toast.success("Aplicado nos campos — regenere o PDF quando quiser"); }}>
                      Só aplicar nos campos
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setApplied((s) => new Set(s).add(i))}>
                      <X className="h-3 w-3 mr-1" /> Ignorar
                    </Button>
                  </div>
                )}
                {applied.has(i) && <div className="text-[11px] text-muted-foreground">Tratado.</div>}
                {!editable && <div className="text-[11px] text-muted-foreground">A proposta não está editável agora.</div>}
              </div>
            )}
          </div>
        ))}
        {chat.isPending && <div className="text-xs text-muted-foreground flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" /> Pensando…</div>}
      </div>

      <div className="border-t p-2 flex gap-1.5 items-end">
        <Textarea
          rows={2}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(input); } }}
          placeholder="Ex.: encurta a apresentação e cita o edital…"
          className="text-sm min-h-[40px] resize-none"
          maxLength={4000}
        />
        <Button size="icon" className="h-9 w-9 shrink-0" disabled={!input.trim() || chat.isPending} onClick={() => send(input)}>
          {chat.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}
